import { useRef, useState, useCallback, useEffect } from 'react';
import { sendChunk, sendFinalChunk, type ChunkResponse } from '@/api/sttChunkApi';

export type SttChunkRecorderState = {
  isListening: boolean;
  error: string | null;
  lastChunkText: string | null;
  finalTranscript: string | null;
};

type UseSttChunkRecorderOptions = {
  interviewId: string;
};

// Configuration constants
const SAMPLE_RATE = 16000;
const CHUNK_INTERVAL_MS = 6000; // 6 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const MAX_IN_FLIGHT_REQUESTS = 3;

const INLINE_STT_WORKLET_SOURCE = `
class SttProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input[0] && input[0].length) {
      const channelData = input[0];
      const copy = new Float32Array(channelData.length);
      copy.set(channelData);
      this.port.postMessage(copy, [copy.buffer]);
    }
    return true;
  }
}
registerProcessor('stt-processor', SttProcessor);
`;

function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i] ?? 0;
    const s = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return buffer;
}

function mergeFloat32Arrays(arrays: Float32Array[]): Float32Array {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    let chunk = '';
    const end = Math.min(bytes.length, i + chunkSize);
    for (let j = i; j < end; j++) {
      chunk += String.fromCharCode(bytes[j] ?? 0);
    }
    binary += chunk;
  }

  return btoa(binary);
}

function encodeChunksToBase64(chunks: Float32Array[], sampleRate: number): string | null {
  if (chunks.length === 0) {
    return null;
  }

  const mergedSamples = mergeFloat32Arrays(chunks);
  if (mergedSamples.length === 0) {
    return null;
  }

  const wavBuffer = encodeWav(mergedSamples, sampleRate);
  return arrayBufferToBase64(wavBuffer);
}

function buildSilentChunk(durationMs: number, sampleRate: number): Float32Array {
  const frameCount = Math.max(1, Math.floor((sampleRate * durationMs) / 1000));
  return new Float32Array(frameCount); // zeros = silence
}

export function useSttChunkRecorder(options: UseSttChunkRecorderOptions) {
  const { interviewId } = options;

  const [state, setState] = useState<SttChunkRecorderState>({
    isListening: false,
    error: null,
    lastChunkText: null,
    finalTranscript: null,
  });

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null); // fallback for browsers where AudioWorklet fails
  const silentGainRef = useRef<GainNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const chunkIndexRef = useRef<number>(0);
  const audioBufferRef = useRef<Float32Array[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingRequestsRef = useRef<Map<number, Promise<ChunkResponse>>>(new Map());
  const isListeningRef = useRef<boolean>(false);
  const isStartingRef = useRef<boolean>(false);
  const startPromiseRef = useRef<Promise<void> | null>(null);

  const interviewIdRef = useRef(interviewId);
  useEffect(() => {
    interviewIdRef.current = interviewId;
  }, [interviewId]);

  const sendChunkToApi = useCallback(
    async (audio: string, chunkIndex: number, isFinal: boolean): Promise<ChunkResponse> => {
      const interviewId = interviewIdRef.current;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const request = {
            id: interviewId,
            audio,
            chunkIndex,
            mimetype: 'audio/wav',
          };

          const result = isFinal ? await sendFinalChunk(request) : await sendChunk(request);

          if (!result.success) {
            throw new Error(result.error || 'Request failed');
          }

          return result;
        } catch (error) {
          lastError = error as Error;
          if (attempt < MAX_RETRIES - 1) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          }
        }
      }

      throw lastError || new Error('Failed to send chunk');
    },
    []
  );

  const cleanupAudioGraph = useCallback(async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }

    if (silentGainRef.current) {
      silentGainRef.current.disconnect();
      silentGainRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close();
    }
    audioContextRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const processAndSendChunk = useCallback(() => {
    if (!isListeningRef.current || audioBufferRef.current.length === 0) {
      return;
    }

    if (pendingRequestsRef.current.size >= MAX_IN_FLIGHT_REQUESTS) {
      return;
    }

    const chunks = [...audioBufferRef.current];
    audioBufferRef.current = [];

    const base64 = encodeChunksToBase64(chunks, SAMPLE_RATE);
    if (!base64) {
      return;
    }

    const currentIndex = chunkIndexRef.current;
    chunkIndexRef.current++;

    const sendPromise = (async () => {
      try {
        const result = await sendChunkToApi(base64, currentIndex, false);

        if (result.text) {
          setState((prev) => ({ ...prev, lastChunkText: result.text ?? null }));
        }

        return result;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setState((prev) => ({ ...prev, error: errorMsg }));
        throw error;
      } finally {
        pendingRequestsRef.current.delete(currentIndex);
      }
    })();

    pendingRequestsRef.current.set(currentIndex, sendPromise);
  }, [sendChunkToApi]);

  const startListening = useCallback(async () => {
    if (isStartingRef.current || isListeningRef.current) {
      return;
    }

    isStartingRef.current = true;

    const startPromise = (async () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      try {
        setState({
          isListening: false,
          error: null,
          lastChunkText: null,
          finalTranscript: null,
        });
        chunkIndexRef.current = 0;
        audioBufferRef.current = [];
        pendingRequestsRef.current.clear();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        streamRef.current = stream;

        const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        let graphReady = false;

        // Preferred path: AudioWorklet (file URL then inline blob fallback)
        let blobUrl: string | null = null;
        try {
          const moduleUrl = new URL('../interview-engine/hooks/worklets/sttProcessor.js', import.meta.url);
          await audioContext.audioWorklet.addModule(moduleUrl);
          graphReady = true;
        } catch (err) {
          console.error('[STT] AudioWorklet module fetch failed, trying inline blob fallback', err);
          try {
            blobUrl = URL.createObjectURL(
              new Blob([INLINE_STT_WORKLET_SOURCE], { type: 'application/javascript' }),
            );
            await audioContext.audioWorklet.addModule(blobUrl);
            graphReady = true;
          } catch (inlineErr) {
            console.error('[STT] Inline AudioWorklet load failed, using ScriptProcessor fallback', inlineErr);
          }
        }

        if (graphReady) {
          const workletNode = new AudioWorkletNode(audioContext, 'stt-processor', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            channelCount: 1,
          });
          workletNodeRef.current = workletNode;

          workletNode.port.onmessage = (event) => {
            if (!isListeningRef.current) return;
            const data = event.data as Float32Array;
            audioBufferRef.current.push(new Float32Array(data));
          };

          const silentGain = audioContext.createGain();
          silentGain.gain.value = 0;
          silentGainRef.current = silentGain;

          source.connect(workletNode);
          workletNode.connect(silentGain);
          silentGain.connect(audioContext.destination);
        } else {
          // Fallback path: ScriptProcessor (legacy)
          const processor = audioContext.createScriptProcessor(4096, 1, 1);
          processorRef.current = processor;

          processor.onaudioprocess = (e) => {
            if (isListeningRef.current) {
              const inputData = e.inputBuffer.getChannelData(0);
              audioBufferRef.current.push(new Float32Array(inputData));
            }
          };

          source.connect(processor);
          processor.connect(audioContext.destination);
        }

        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }

        isListeningRef.current = true;
        setState((prev) => ({ ...prev, isListening: true }));

        intervalRef.current = setInterval(() => {
          if (isListeningRef.current) {
            processAndSendChunk();
          }
        }, CHUNK_INTERVAL_MS);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to start recording';
        setState((prev) => ({ ...prev, error: errorMsg }));
        await cleanupAudioGraph();
      } finally {
        isStartingRef.current = false;
        startPromiseRef.current = null;
      }
    })();

    startPromiseRef.current = startPromise;
    return startPromise;
  }, [cleanupAudioGraph, processAndSendChunk]);

  const doneListening = useCallback(async (): Promise<string | null> => {
    const hasResources =
      isListeningRef.current ||
      isStartingRef.current ||
      streamRef.current !== null ||
      audioContextRef.current !== null ||
      pendingRequestsRef.current.size > 0 ||
      audioBufferRef.current.length > 0;

    if (!hasResources) {
      return null;
    }

    if (startPromiseRef.current) {
      try {
        await startPromiseRef.current;
      } catch {
        // ignore start errors, already reflected in state
      }
    }

    isListeningRef.current = false;

    const hadActiveSession =
      isStartingRef.current ||
      streamRef.current !== null ||
      audioContextRef.current !== null ||
      chunkIndexRef.current > 0;

    await cleanupAudioGraph();

    setState((prev) => ({ ...prev, isListening: false }));

    const pendingPromises = Array.from(pendingRequestsRef.current.values());
    if (pendingPromises.length > 0) {
      await Promise.allSettled(pendingPromises);
    }

    const remainingChunks = [...audioBufferRef.current];
    audioBufferRef.current = [];

    const finalBase64 = encodeChunksToBase64(remainingChunks, SAMPLE_RATE);
    const shouldSendFinal = finalBase64 !== null || chunkIndexRef.current > 0 || hadActiveSession;
    const finalIndex = chunkIndexRef.current;

    if (!shouldSendFinal) {
      pendingRequestsRef.current.clear();
      return null;
    }

    try {
      const safeFinalBase64 =
        finalBase64 ??
        encodeChunksToBase64([buildSilentChunk(300, SAMPLE_RATE)], SAMPLE_RATE) ??
        '';

      const result = await sendChunkToApi(safeFinalBase64, finalIndex, true);

      if (result.accumulatedText) {
        setState((prev) => ({
          ...prev,
          finalTranscript: result.accumulatedText ?? null,
        }));
      }
      return result.accumulatedText ?? null;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setState((prev) => ({ ...prev, error: errorMsg }));
      return null;
    } finally {
      pendingRequestsRef.current.clear();
    }
  }, [cleanupAudioGraph, sendChunkToApi]);

  useEffect(() => {
    return () => {
      isListeningRef.current = false;
      void cleanupAudioGraph();
    };
  }, [cleanupAudioGraph]);

  return {
    startListening,
    doneListening,
    ...state,
  };
}
